import {
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpErrorResponse } from '@angular/common/http';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AbstractDataService } from '@nimmsta/ng-commons-lib/data-access';
import {
  DataConnection,
  DataConnectionType,
} from '@nimmsta/ng-commons-lib/models';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { CsvFormComponent } from './types/csv/csv-form.component';
import { HttpFormComponent } from './types/http/http-form.component';
import { HttpDataConnection } from './types/http/httpDataConnection';
import { CsvDataConnection } from './types/csv/csvDataConnection';
import { DataConnectionService } from '../services/dataConnection.service';
import { DataConnectionTemplateService } from '../services/dataConnectionTemplate.service';
import { TranslatePipe } from '@ngx-translate/core';
import { LaddaModule } from 'angular2-ladda';
import { SelectModule } from 'primeng/select';
import { SortingFormComponent } from './types/sorting/sorting-form.component';

_('dataconnection.CSVDataConnection');
_('dataconnection.WeclappDataConnection');
_('dataconnection.HttpDataConnection');
_('dataconnection.SortingDataConnection');
_('SortingDataConnection');
_('dataconnection.title');
_('dataconnection.identifier');
_('CSVDataConnection');
_('HttpDataConnection');
_('WeclappDataConnection');

@Component({
  selector: 'app-dataconnection-form',
  templateUrl: 'dataconnection-form.component.html',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormsModule,
    CsvFormComponent,
    HttpFormComponent,
    SortingFormComponent,
    ErrorComponent,
    LaddaModule,
    TranslatePipe,
    SelectModule,
  ],
})
export class DataConnectionFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  modalRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(
    DynamicDialogConfig<{ value: DataConnection; isTemplate?: boolean }>,
  );
  private dataService = inject(DataConnectionService);
  private dataTemplateService = inject(DataConnectionTemplateService);

  readonly DataConnectionType = DataConnectionType;

  dataConnectionTypes: DataConnectionType[] = [];

  @ViewChild(CsvFormComponent)
  private csvForm?: CsvFormComponent;

  @ViewChild(HttpFormComponent)
  private httpForm?: HttpFormComponent;

  @ViewChild(SortingFormComponent)
  private sortingForm?: SortingFormComponent;

  value: DataConnection = {} as DataConnection;

  isTemplate = false;

  loading = false;

  error?: HttpErrorResponse;

  ngOnInit(): void {
    Object.assign(this, this.dialogConfig.data);
    this.dataService
      .getAllDataConnectionsSupported()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (connectionTypes) => {
          this.dataConnectionTypes = connectionTypes;
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
        },
      });
  }

  save(closeAfterSave = true) {
    const form = this.getCurrentForm()?.form as FormGroup | undefined;
    if (!form || this.loading) {
      return;
    }
    if (
      this.value.id &&
      form.pristine &&
      form.valid &&
      !form.touched &&
      closeAfterSave
    ) {
      this.modalRef.close();
      return;
    }
    if (form.valid) {
      this.error = undefined;
      this.loading = true;

      this.value = Object.assign(this.value, form.value);

      if (
        this.value.dataConnectionHandler ===
        DataConnectionType.CSVDataConnection
      ) {
        this.value.configuration = this.csvForm!.getConfiguration();
      }
      if (
        this.value.dataConnectionHandler ===
        DataConnectionType.SortingDataConnection
      ) {
        this.value.configuration = this.sortingForm!.getConfiguration();
        // Sorting get/delete endpoints are supplied by the backend handler.
        this.value.endpoints = [];
      }
      // config has to be stringified
      if (this.value.configuration instanceof Object) {
        this.value.configuration = JSON.stringify(this.value.configuration);
      }
      form.disable();

      this.service.createOrUpdate(this.value).subscribe({
        next: (value) => {
          this.value = value;
          if (closeAfterSave) {
            this.modalRef.close();
          }
          form.markAsPristine();
          form.enable();
          this.loading = false;
        },
        error: (response) => {
          this.error = response;
          form.enable();
          this.loading = false;
        },
      });
    } else {
      form.markAllAsTouched();
    }
  }

  getCurrentForm():
    CsvFormComponent | HttpFormComponent | SortingFormComponent | undefined {
    switch (this.value.dataConnectionHandler) {
      case DataConnectionType.CSVDataConnection:
        return this.csvForm;
      case DataConnectionType.HttpDataConnection:
        return this.httpForm;
      case DataConnectionType.SortingDataConnection:
        return this.sortingForm;
      default:
        return undefined;
    }
  }

  asCsvDataConnection(): CsvDataConnection {
    return this.value as CsvDataConnection;
  }

  asHttpDataConnection(): HttpDataConnection {
    return this.value as HttpDataConnection;
  }

  private get service(): AbstractDataService<DataConnection> {
    return this.isTemplate ? this.dataTemplateService : this.dataService;
  }

  get selectableTypes(): DataConnectionType[] {
    if (this.isTemplate) {
      return [
        DataConnectionType.CSVDataConnection,
        DataConnectionType.HttpDataConnection,
        ...(this.dataConnectionTypes.includes(
          DataConnectionType.SortingDataConnection,
        )
          ? [DataConnectionType.SortingDataConnection]
          : []),
      ];
    }
    return this.dataConnectionTypes;
  }
}
